import { defineStore } from 'pinia'
import type { TApiResponse } from '@/types/api-types/TApiResponse'
import AccountApiRequest from '@/api/account'
import { DASHBOARD_CLIENT_TYPE, WORKER_SIGNAL_R } from '@/utils/constants'
import AuthService, { clearStorageAndRefresh } from '@/services/AuthService'
import { logError } from '@/utils/helpers/logger'
import { User } from '@/models'
import StatusCodeType from '@/enum/StatusCodeType'
import ErrorHandlerService from '@/services/ErrorHandlerService'
import ErrorType from '@/enum/ErrorType'
import ResponseTypeMapper from '@/mappers/ResponseMapper'
import type { TPermission } from '@/types/api-types/account'
import { saveDataToCookieStorage, saveUserIdToLocalStorage } from '@/utils/helpers'
// import ConfigsApiRequest from '@/api/configs';
// import ValidationConfig from '@/models/ValidationsConfig';
import useGlobalStore from '../global'
import state from './state'
import getters from './getters'
import { clearLocalStorageAndCookies } from '@/utils/helpers/app-helpers'
import useWorkersStore from '../workers'
import ConfigApiRequest from '@/api/config'
import { markRaw } from 'vue'
import Config from '@/models/Config'
import NotificationType from '@/enum/NotificationType'
import CONSTANTS from '@/utils/constants/string-constants'

const useAccountStore = defineStore('account', {
  state: () => state,
  getters,
  actions: {
    async mockLoginUser(email: string, password: string) {
      let mockData: any = {}

      const storeUserMockData = () => {
        mockData = {
          userId: 'mockUserId',
          accessToken: 'mockAccessToken',
          accessTokenExpiresInSeconds: 99999,
          refreshToken: 'mockRefreshToken',
          refreshTokenExpiresInSeconds: 99999,
          userRole: 'Admin',
          accessTokenExpirationUtc: '2025-09-17T12:15:18.249Z',
          refreshTokenExpirationUtc: '2025-09-17T12:15:18.249Z'
        }

        saveDataToCookieStorage(mockData)
        saveUserIdToLocalStorage(mockData.userId)

        const globalStore = useGlobalStore()
        globalStore.setAccountData(mockData)
      }

      return new Promise((resolve) => {
        setTimeout(() => {
          storeUserMockData()
          resolve({
            status: StatusCodeType.Success,
            data: mockData
          })
        }, 0)
      })
    },
    async loginUser(email: string, password: string): Promise<TApiResponse | undefined> {
      let response
      const accountApi = new AccountApiRequest()
      try {
        response = await accountApi.requestAccess({
          username: email,
          password,
          clientAppKey: DASHBOARD_CLIENT_TYPE
        })
        return response
      } catch (error) {
        logError('Error during login: ', error)
        throw error
      }
    },
    async logoutUser() {
      const accountApi = new AccountApiRequest()

      const formData = new FormData()
      formData.append('clientAppKey', DASHBOARD_CLIENT_TYPE)

      const response = await accountApi
        .revokeAccess(formData as any)
        .catch((error: any) => {
          logError('Error during logout: ', error)
        })
        .finally(() => {
          const workersStore = useWorkersStore()
          workersStore.killWorker(WORKER_SIGNAL_R)
          clearStorageAndRefresh({ preserveItemsList: ['version'] })
        })
      return response
    },
    async getAccountPermissions() {
      const accountApi = new AccountApiRequest(true)
      const response = await accountApi.getPermissions()
      if (response && response.status === StatusCodeType.Unauthorized) return
      if (response) {
        const mappedResponse = ResponseTypeMapper.CollectionResponseMapper<TPermission>(response)
      }
    },
    async getProfile(id: string) {
      const accountApi = new AccountApiRequest(true)
      try {
        const responsePromise = accountApi.getProfile(id)
        this.profilePromise = responsePromise
        const response = await responsePromise
        if (response && response.status === StatusCodeType.Success && response.data) {
          this.profile = new User(response.data)
        } else {
          console.error('User not logged in!')
        }
      } catch (error: any) {
        ErrorHandlerService.handleError({ name: error.message }, ErrorType.Component).catch(
          (error) => error
        )
        await this.logoutUser()
      }
    },
    async getValidationConfig() {
      const globalStore = useGlobalStore()
      // const configApi = new ConfigsApiRequest(true);
      // const response = await configApi.getValidations();
      // if (response && response.status === StatusCodeType.Success && response.data) {
      //   const config = new ValidationConfig(response.data);
      //   globalStore.validationConfig = config;

      //   if (process.env.NODE_ENV === 'development') {
      //     globalStore.validationConfig.patrolSettingsDefault.expectedTimeInMinutes.default = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.expectedTimeInMinutes.min = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.surveySendTimeInMinutes.default = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.surveySendTimeInMinutes.min = 1;
      //     globalStore.validationConfig.patrolSettingsDefault.defaultMinimumTimeInMinutes = 0;
      //   }
      // }
    },
    async requestPasswordReset(email: string) {
      const accountApi = new AccountApiRequest()
      const requestBody = new FormData()
      requestBody.set('email', email)
      requestBody.set('clientAppKey', DASHBOARD_CLIENT_TYPE)
      const result = await accountApi.resetPassword(requestBody)
      return result
    },
    async confirmNewPassword(email: string, password: string, token: string) {
      const accountApi = new AccountApiRequest()
      const requestBody = new FormData()
      requestBody.set('email', email)
      requestBody.set('newPassword', password)
      requestBody.set('token', token)
      requestBody.set('clientAppKey', DASHBOARD_CLIENT_TYPE)
      const result = await accountApi.confirmNewPassword(requestBody)
      return result
    },
    resetAndClearApp() {
      clearLocalStorageAndCookies({ preserveItemsList: ['version'] })
    },
    async resetConfigForm() {
      this.configForm = new Config()
    },
    async getPricesConfigDetails() {
      this.isLoadingPriceConfig = true
      try {
        const configApi = new ConfigApiRequest()
        const result = await configApi.getConfigDetails()
        if (result.status === StatusCodeType.Success && result.data) {
          this.configForm = new Config(result.data)
        }
      } catch (error: any) {
        ErrorHandlerService.handleError(error, error)
      } finally {
        this.isLoadingPriceConfig = false
      }
    },
    async createNewConfigPrice(): Promise<{ status: StatusCodeType }> {
      const configApi = new ConfigApiRequest()
      const configForm = markRaw(new Config(this.configForm))
      const globalStore = useGlobalStore()
      const result = await configApi.addPriceConfig(configForm)
      if (result.status === StatusCodeType.Success) {
        globalStore.toggleConfigFlyout()
        globalStore.addToastNotification({
          id: Symbol(`Service`),
          message: `Config price ${CONSTANTS.UPDATED_SUCCESSFULLY}`,
          type: NotificationType.Success
        })
      }
      return result
    }
  }
})

export default useAccountStore
